// 縦に長い表の「見えている行だけ描く」ための純関数。 行の高さは内容 (違反明細の行数) で
// 変わるので、 測れた行は実測値、 測れていない行は推定値で位置を決める。

export type VirtualWindow = {
  /** 描く先頭の行 (含む)。 */
  start: number
  /** 描く末尾の行 (含まない)。 */
  end: number
  /** 先頭より上の行の高さの合計 (px)。 */
  topPad: number
  /** 末尾より下の行の高さの合計 (px)。 */
  bottomPad: number
}

/**
 * 表示領域 (`scrollTop` から `viewportHeight`) に掛かる行 ± `overscan` 行を返す。
 * `heightOf(i)` は i 行目の高さ (実測値か推定値)。
 */
export const computeVirtualWindow = (
  count: number,
  heightOf: (index: number) => number,
  scrollTop: number,
  viewportHeight: number,
  overscan: number,
): VirtualWindow => {
  if (count === 0) return { start: 0, end: 0, topPad: 0, bottomPad: 0 }
  const offsets: number[] = new Array<number>(count + 1)
  offsets[0] = 0
  for (let i = 0; i < count; i += 1) {
    offsets[i + 1] = (offsets[i] ?? 0) + heightOf(i)
  }
  const total = offsets[count] ?? 0
  const top = Math.max(0, scrollTop)
  const bottom = top + Math.max(0, viewportHeight)
  // 表示領域の先頭に掛かる最初の行 / 末尾に掛かる最後の行を二分探索で探す。
  const firstVisible = upperBound(offsets, top, count) - 1
  const lastVisible = lowerBound(offsets, bottom, count)
  const start = Math.max(0, Math.min(count - 1, firstVisible) - overscan)
  const end = Math.min(count, Math.max(start + 1, lastVisible) + overscan)
  return {
    start,
    end,
    topPad: offsets[start] ?? 0,
    bottomPad: total - (offsets[end] ?? total),
  }
}

/** `offsets[i] > value` となる最初の i (見つからなければ count)。 */
const upperBound = (
  offsets: readonly number[],
  value: number,
  count: number,
): number => {
  let lo = 0
  let hi = count
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((offsets[mid] ?? 0) > value) hi = mid
    else lo = mid + 1
  }
  return lo
}

/** `offsets[i] >= value` となる最初の i (見つからなければ count)。 */
const lowerBound = (
  offsets: readonly number[],
  value: number,
  count: number,
): number => {
  let lo = 0
  let hi = count
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((offsets[mid] ?? 0) >= value) hi = mid
    else lo = mid + 1
  }
  return lo
}
